import { URLS } from '@assets/environment/urls'
import { Training } from '@entities/training'
import { AxiosResponse } from 'axios'
import {
	CreateTrainingRequest,
	CreateTrainingResponse,
	DeleteTrainingResponse,
	UpdateTrainingRequest,
	UpdateTrainingResponse,
} from '@entities/training/types/create'
import { createSearchParams } from '@shared/helpers/generateSearchParams'
import { PaginationResult } from '@shared/types/pagination'
import { SearchParams } from '@shared/types/searchParams'
import { authAxios } from '@shared/api/auth-instance'
import { getCurrentBackendLocale } from '@shared/i18n'
import { Language } from '@shared/types/language'

export class TrainingsProxy {
	static BASE_URL: string = URLS.WORKOUT + 'workouts/'

	static getTrainings = async (params: { limit: number; start: number }): Promise<Training[]> => {
		try {
			const response: AxiosResponse<PaginationResult<Training>> = await authAxios.post(
				TrainingsProxy.BASE_URL + 'my/search',
				createSearchParams({ limit: params.limit, start: params.start }),
				{ params: { include_started_system: true } }
			)
			return response.data.rows
		} catch (e) {
			console.log(e)
			return []
		}
	}

	static ftsSearch = async (
		params: SearchParams,
		term: string,
		lang: Language = getCurrentBackendLocale()
	): Promise<Training[]> => {
		const searchParams = new URLSearchParams()
		searchParams.set('term', term)
		searchParams.set('lang', lang)
		searchParams.set('include_started_system', 'true')

		try {
			const response: AxiosResponse<PaginationResult<Training>> = await authAxios.post(
				TrainingsProxy.BASE_URL + 'my/search-fts',
				params,
				{ params: searchParams }
			)
			return response.data.rows
		} catch (e: unknown) {
			console.log(e)
			return []
		}
	}

	static createTraining = async (
		params: CreateTrainingRequest
	): Promise<CreateTrainingResponse> => {
		const response: AxiosResponse<CreateTrainingResponse> =
			await authAxios.post<CreateTrainingResponse>(TrainingsProxy.BASE_URL + 'create', params)
		return response.data
	}

	static deleteTraining = async (trainingId: number): Promise<DeleteTrainingResponse> => {
		const response: AxiosResponse<DeleteTrainingResponse> =
			await authAxios.delete<DeleteTrainingResponse>(TrainingsProxy.BASE_URL + `${trainingId}`)
		return response.data
	}

	static updateTraining = async (
		workoutId: number,
		params: UpdateTrainingRequest
	): Promise<UpdateTrainingResponse> => {
		const response: AxiosResponse<UpdateTrainingResponse> =
			await authAxios.put<UpdateTrainingResponse>(
				TrainingsProxy.BASE_URL + `${workoutId}`,
				params
			)
		return response.data
	}

	static cloneTraining = async (workoutId: number): Promise<boolean> => {
		try {
			const response = await authAxios.post(TrainingsProxy.BASE_URL + `${workoutId}/copy`)
			return response.status < 400
		} catch (e) {
			console.log(e)
			return false
		}
	}

	public static getTrainingById = async (workoutId: number): Promise<Training | null> => {
		try {
			const response: AxiosResponse<Training> = await authAxios.get<Training>(
				TrainingsProxy.BASE_URL + `${workoutId}`
			)
			return response.data
		} catch (e: unknown) {
			console.log('Error fetching training by ID:', e)
			return null
		}
	}
}
